import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Col, UiButton, UiQuestionnaireLayout, UiTag, UiText} from '~/components/elements';
import BoldText from '~/components/shared/BoldText';
import MedicationCard from '~/components/shared/MedicationCard';
import ProgramCard from '~/components/shared/ProgramCard';
import {PAGE_URLS} from '~/constants';
import {
  LET_DOCTOR_DECIDE_THRESHOLD,
  PROGRAM_TYPE,
  WEIGHT_LOSS_KEYS,
  WEIGHT_LOSS_ONE_QUESTIONNAIRES,
  WL_PROGRAMS,
} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {Program} from '~/types/models/questionnaire';

interface Props {}

const Questionnaire7NewScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.PROGRAM_SELECTION);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.PROGRAM_SELECTION]?.data,
  );

  const bmiAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.BMI_CALCULATOR]?.data,
  ) || {height: 0, weight: 0, bmi: 0};

  const showDoctorDecide = bmiAnswer.bmi >= LET_DOCTOR_DECIDE_THRESHOLD;

  const {inputs, setInputs, validation} = useInput({
    selectedProgram: currentAnswer?.selectedProgram || '',
    selectedProgramLocal: currentAnswer?.selectedProgramLocal || '',
  });

  const collectedData = mdq.collectedData;

  const onClickDoctorDecide = () => {
    setInputs({
      selectedProgram: WL_PROGRAMS.LET_DOCTOR_DECIDE.value,
      selectedProgramLocal: WL_PROGRAMS.LET_DOCTOR_DECIDE.localValue,
    });
    onClickSubmit({
      selectedProgram: WL_PROGRAMS.LET_DOCTOR_DECIDE.value,
      selectedProgramLocal: WL_PROGRAMS.LET_DOCTOR_DECIDE.localValue,
    });
  };

  const onClickSubmit = (data?: {selectedProgram: string; selectedProgramLocal: string}) => {
    const currentLocalSelectedProgram = data?.selectedProgramLocal || inputs.selectedProgramLocal;
    const answerData = {
      key: WEIGHT_LOSS_KEYS.PROGRAM_SELECTION,
      index: questionnaire?.index || '',
      data: data
        ? data
        : {
            selectedProgram: inputs.selectedProgram,
            selectedProgramLocal: inputs.selectedProgramLocal,
          },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.PROGRAM_SELECTION]: answerData,
          },
        },
      }),
    );

    switch (currentLocalSelectedProgram) {
      case WL_PROGRAMS.ELEVATE_CGM_PROGRAM.localValue:
        history.push(PAGE_URLS.MDQ1_GREAT_NEWS_CGM);
        break;
      case WL_PROGRAMS.ELEVATE_SUPPLEMENT_PROGRAM.localValue:
        history.push(PAGE_URLS.MDQ1_GREAT_NEWS_SUPPLEMENTS);
        break;
      case WL_PROGRAMS.LET_DOCTOR_DECIDE.localValue:
        history.push(PAGE_URLS.MDQ1_DOCTOR_CONSULTATION);
        break;
      case WL_PROGRAMS.ELEVATE_PANBESY.localValue:
        history.push(PAGE_URLS.MDQ1_SELECT_PANBESY_PACKAGE);
        break;
      case WL_PROGRAMS.ELEVATE_NUTRITION_PROGRAM.localValue:
        history.push(PAGE_URLS.MDQ1_NUTRITION_HOW_IT_WORKS);
        break;
      default:
        history.push(PAGE_URLS.MDQ1_SYMPTOMS_CHECK);
        break;
    }
  };

  const onGoBack = () => {
    if (collectedData[WEIGHT_LOSS_KEYS.PROGRAM_SELECTION]) {
      const answerData = {
        key: WEIGHT_LOSS_KEYS.PROGRAM_SELECTION,
        index: questionnaire?.index || '',
        data: {},
        order: answerOrder,
      };
      dispatch(setQuestionnaireAnswer(answerData));
    }
  };

  const renderProgram = (program: Program) => {
    const isSelected = inputs.selectedProgramLocal === program.localValue;

    if (program?.range && (bmiAnswer.bmi < program.range[0] || bmiAnswer.bmi > program.range[1])) {
      return null;
    }

    if (program.type === PROGRAM_TYPE.MEDICATION) {
      return (
        <MedicationCard
          key={program.localValue}
          program={program}
          isSelected={isSelected}
          onClick={value =>
            setInputs({
              selectedProgram: value,
              selectedProgramLocal: program.localValue,
            })
          }
        />
      );
    }

    if (program.type === PROGRAM_TYPE.PROGRAM) {
      return (
        <ProgramCard
          key={program.localValue}
          program={program}
          isSelected={isSelected}
          onClick={value =>
            setInputs({
              selectedProgram: value,
              selectedProgramLocal: program.localValue,
            })
          }
        />
      );
    }

    return null;
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      actions={questionnaire?.actions}
      disableNext={
        !inputs?.selectedProgram || inputs?.selectedProgramLocal === WL_PROGRAMS.LET_DOCTOR_DECIDE.localValue
      }
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      pageTitle={questionnaire?.title}
      handleGoBack={onGoBack}
    >
      <Col textAlign="center" mt={7} px={2}>
        <BoldText
          text={questionnaire?.questions?.default || ''}
          fontSize="xl"
          fontWeight="bold"
          boldTextStyles={{color: '#8DAA09'}}
          mb={4}
        />
        {showDoctorDecide && (
          <UiText alignSelf="center" fontSize="sm" color="liteOuter" mb={4} maxW={72}>
            {questionnaire?.questions?.sub}
          </UiText>
        )}

        <Col alignItems="center" justifyContent="center" width="100%">
          {questionnaire?.programs?.map(program => {
            return renderProgram(program);
          })}
        </Col>

        {showDoctorDecide && (
          <Col w="60%" alignSelf="center" mb={6}>
            <UiText fontSize="sm" color="liteOuter" mb={4}>
              {questionnaire?.questions?.sub2}
            </UiText>
            <UiButton
              // background={inputs.selectedProgram === WL_PROGRAMS.LET_DOCTOR_DECIDE.value ? 'lightBaseColor' : '#F2EEE4'}
              background="#F2EEE4"
              _hover={{}}
              textColor="trueBlack"
              onClick={onClickDoctorDecide}
            >
              Let the doctor decide
            </UiButton>
          </Col>
        )}

        <UiText color="liteOuter" fontSize="10px">
          {showDoctorDecide && questionnaire?.questions?.sub3}
        </UiText>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7NewScreen;
